import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import DataTable, { Column } from "@/components/ui/DataTable";
import { Button, ButtonAdd } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useGetPurchaseOrdersQuery,
  useLazyGetPurchaseReportCsvQuery,
  PurchaseOrderSortField,
} from "@/redux/services/inventory/purchaseOrder.api";
import { downloadBlob, exceedsReportRange } from "@/lib/reportDownload";
import { useGetSupplierQuery } from "@/redux/services/inventory/supplier.api";
import {
  PurchaseOrder,
  PurchaseOrderStatus,
} from "@/redux/services/inventory/inventory.type";
import { displayPrice } from "@/lib/utils";
import { usePagination, useRestaurantCurrency } from "@/lib/hooks";
import { useDebounce } from "@/hooks/useDebounce";
import { usePermissions } from "@/hooks/usePermissions";
import { canRecordPurchaseOrders } from "@/lib/permissions";
import { format, parseISO, subDays } from "date-fns";
import { fr } from "date-fns/locale";
import { FileSpreadsheet, X } from "lucide-react";
import PurchaseOrderStatusBadge from "./PurchaseOrderStatusBadge";
import OverduePill, { isPurchaseOrderOverdue } from "./OverduePill";
import { DateRangePicker } from "@/components/Dashboard/DateRangePicker";
import { SearchIgnoresFiltersHint } from "@/components/common/SearchIgnoresFiltersHint";
import { filtersUnlessSearching } from "@/lib/searchScope";

interface PurchaseOrdersListProps {
  restaurantId: string;
}

const STATUSES: PurchaseOrderStatus[] = [
  "draft",
  "sent",
  "partially_received",
  "received",
  "cancelled",
];

const STATUS_LABELS: Record<PurchaseOrderStatus, string> = {
  draft: "Brouillon",
  sent: "Envoyé",
  partially_received: "Partiellement reçu",
  received: "Reçu",
  cancelled: "Annulé",
};

const SENTINEL_ALL = "__all__";

// The picker is always a closed range; default to the last 7 days.
const RANGE_DAYS = 7;
const defaultRange = () => ({
  start: format(subDays(new Date(), RANGE_DAYS - 1), "yyyy-MM-dd"),
  end: format(new Date(), "yyyy-MM-dd"),
});

const PurchaseOrdersList = ({ restaurantId }: PurchaseOrdersListProps) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const supplierUuid = searchParams.get("supplier") ?? undefined;
  const { page, limit, onChange } = usePagination(25);
  const [status, setStatus] = useState<PurchaseOrderStatus | undefined>();
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 300);
  const [startDate, setStartDate] = useState(() => defaultRange().start);
  const [endDate, setEndDate] = useState(() => defaultRange().end);
  const [sortBy, setSortBy] = useState<PurchaseOrderSortField>("orderDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const currency = useRestaurantCurrency();
  const { can } = usePermissions();
  // Purchase orders belong to the suppliers domain in the API
  // (PERMISSION_SUPPLIERS: suppliers.view to read, suppliers.manage to write).
  // The purchase form also lists ingredients (inventory.view), and the CSV is
  // the purchase report (reports.view) (SFRESTO-165).
  const canRecordPurchases = canRecordPurchaseOrders(can);
  const canExportCsv = can("reports.view");

  useEffect(() => {
    onChange(limit, 1);
  }, [debouncedSearch, startDate, endDate, status]);

  const { data: supplierData } = useGetSupplierQuery(
    { supplierUuid: supplierUuid ?? "" },
    { skip: !supplierUuid }
  );

  const { data, isFetching } = useGetPurchaseOrdersQuery({
    restaurantId,
    query: {
      page,
      limit,
      ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
      // A search term looks across all dates and ignores the status and
      // supplier filters; the filters scope browsing only.
      ...filtersUnlessSearching(debouncedSearch, {
        status,
        supplierUuid,
        orderDateAfter: `${startDate}T00:00:00Z`,
        orderDateBefore: `${endDate}T23:59:59Z`,
      }),
      sortBy,
      sortDirection,
    },
  });

  const [triggerCsv, { isFetching: isExporting }] =
    useLazyGetPurchaseReportCsvQuery();

  // Export the received purchases over the selected period. A custom picker
  // range can exceed the report's 365-day cap (both dates inclusive, like the
  // API, SFAPI-156), so guard it before the request.
  const handleExportCsv = async () => {
    if (exceedsReportRange(startDate, endDate)) {
      toast.error("La période ne peut pas dépasser 365 jours.");
      return;
    }
    try {
      const { blob, filename } = await triggerCsv({
        restaurantId,
        startDate,
        endDate,
      }).unwrap();
      downloadBlob(
        blob,
        filename ?? `purchase_report_${startDate}_${endDate}.csv`
      );
    } catch {
      toast.error("Erreur lors du téléchargement du CSV");
    }
  };

  const clearSupplierFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("supplier");
    setSearchParams(next, { replace: true });
    onChange(limit, 1);
  };

  const isDefaultRange =
    startDate === defaultRange().start && endDate === defaultRange().end;
  const hasActiveFilters = !!status || !isDefaultRange;

  const clearFilters = () => {
    setStatus(undefined);
    const { start, end } = defaultRange();
    setStartDate(start);
    setEndDate(end);
    onChange(limit, 1);
  };

  const columns: Column[] = [
    {
      key: "poNumber",
      label: "N°",
      formatter: (_v, row: PurchaseOrder) => (
        <Link
          to={`/inventory/purchase-orders/${row.id}`}
          className="text-gray-900 hover:text-blue-700 hover:underline font-medium"
        >
          {row.poNumber}
        </Link>
      ),
    },
    {
      key: "supplier",
      label: "Fournisseur",
      formatter: (_v, row: PurchaseOrder) => row.supplier?.name ?? "—",
    },
    {
      key: "status",
      label: "Statut",
      formatter: (_v, row: PurchaseOrder) => (
        <div className="flex items-center gap-1.5 flex-wrap">
          <PurchaseOrderStatusBadge status={row.status} />
          {isPurchaseOrderOverdue(row) && <OverduePill />}
        </div>
      ),
    },
    {
      key: "orderDate",
      label: "Date",
      sortable: true,
      nowrap: true,
      formatter: (_v, row: PurchaseOrder) =>
        format(parseISO(row.orderDate), "d MMM yyyy", { locale: fr }),
    },
    {
      key: "expectedDeliveryDate",
      label: "Livraison prévue",
      nowrap: true,
      formatter: (_v, row: PurchaseOrder) =>
        row.expectedDeliveryDate
          ? format(parseISO(row.expectedDeliveryDate), "d MMM yyyy", {
              locale: fr,
            })
          : "—",
    },
    {
      key: "receivedAt",
      label: "Reçu le",
      sortable: true,
      nowrap: true,
      formatter: (_v, row: PurchaseOrder) =>
        row.receivedAt
          ? format(parseISO(row.receivedAt), "d MMM yyyy", { locale: fr })
          : "—",
    },
    {
      key: "totalAmount",
      label: "Total",
      sortable: true,
      formatter: (_v, row: PurchaseOrder) => (
        <span className="font-medium">
          {displayPrice(row.totalAmount, currency)}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-3">
      {supplierUuid && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">Filtre :</span>
          <span className="inline-flex items-center gap-1.5 bg-primary-light text-primary px-2.5 py-1 rounded-full text-xs font-medium">
            Fournisseur : {supplierData?.name ?? "…"}
            <button
              type="button"
              onClick={clearSupplierFilter}
              className="hover:bg-primary/10 rounded-full p-0.5"
              aria-label="Retirer le filtre"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        </div>
      )}

      <div className="flex justify-end gap-2">
        {canExportCsv && (
          <Button
            type="button"
            variant="outline"
            onClick={handleExportCsv}
            disabled={isExporting}
            isLoading={isExporting}
            title="Exporter les achats reçus de la période sélectionnée (CSV)"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Exporter (CSV)
          </Button>
        )}
        {canRecordPurchases && (
          <Link to="/inventory/purchase-orders/new">
            <ButtonAdd label="Saisir des achats" type="button" />
          </Link>
        )}
      </div>

      <div className="dash__card space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-card-foreground">Filtres</h3>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1 text-xs font-medium text-danger hover:underline"
            >
              <X className="h-3.5 w-3.5" />
              Réinitialiser les filtres
            </button>
          )}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="sm:flex-1">
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Période
            </label>
            <DateRangePicker
              className="w-full"
              startDate={startDate}
              endDate={endDate}
              onDateChange={(s, e) => {
                setStartDate(s);
                setEndDate(e);
              }}
            />
          </div>
          <div className="sm:w-56">
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Statut
            </label>
            <Select
              value={status ?? SENTINEL_ALL}
              onValueChange={(v) =>
                setStatus(
                  v === SENTINEL_ALL ? undefined : (v as PurchaseOrderStatus)
                )
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SENTINEL_ALL}>Tous les statuts</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <SearchIgnoresFiltersHint search={debouncedSearch} />
      </div>

      <DataTable
        data={data?.results ?? []}
        columns={columns}
        loading={isFetching}
        totalCount={data?.count ?? 0}
        currentPage={page}
        pageSize={limit}
        searchValue={searchInput}
        onSearch={setSearchInput}
        sortBy={sortBy}
        sortOrder={sortDirection}
        onSort={(key, order) => {
          setSortBy(key as PurchaseOrderSortField);
          setSortDirection(order);
          onChange(limit, 1);
        }}
        onPageChange={(p) => onChange(limit, p)}
        onPageSizeChange={(size) => onChange(size, 1)}
        onRowClick={(row: PurchaseOrder) =>
          navigate(`/inventory/purchase-orders/${row.id}`)
        }
        emptyMessage="Aucun bon de commande"
      />
    </div>
  );
};

export default PurchaseOrdersList;
